import { App } from "antd";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { InferType } from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { handleErrors } from "@/shared/utils/forms.js";
import { usePatchUserByIdMutation } from "@/redux/service";
import BaseButton from "@/components_v2/Button/BaseButton";
import BaseSvgRenderer from "@/components_v2/Svg/BaseSvgRenderer";
import FormFieldWrapper from "@/components_v2/Wrapper/FormFieldWrapper";
import { HookFormInput } from "@/components_v2/FormFields/ReactHookForm/fields";
import LockIcon from "@/assets/icons/lock.svg?react";
import { ChangeAccountFormProps } from "@/features/users/settings/types";

import "../styles.css";

const ChangePasswordForm = ({ userId }: ChangeAccountFormProps) => {
  const [updateUser, { isLoading }] = usePatchUserByIdMutation();
  const { notification } = App.useApp();
  const { t } = useTranslation();

  const changePasswordSchema = yup.object().shape({
    oldPassword: yup
      .string()
      .typeError(t("users.validation.oldPasswordNotValid"))
      .required(t("users.validation.oldPasswordRequired")),
    newPassword: yup
      .string()
      .typeError(t("users.validation.newPasswordNotValid"))
      .min(8, t("users.validation.passwordLengthValidation"))
      .matches(/[A-Z]/, t("users.validation.passwordCapitalLetterValidation"))
      .matches(/[0-9]/, t("users.validation.passwordNumberValidation"))
      .required(t("users.validation.newPasswordRequired")),
    confirmPassword: yup
      .string()
      .typeError(t("users.validation.passwordNotValid"))
      .oneOf([yup.ref("newPassword")], t("users.validation.passwordsMustMatch"))
      .required(t("users.validation.passwordRequired")),
  });

  const form = useForm({
    resolver: yupResolver(changePasswordSchema),
    mode: "onChange",
    defaultValues: {
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  const onSubmit = (values: InferType<typeof changePasswordSchema>) => {
    updateUser({ id: userId, data: values })
      .unwrap()
      .then(() => {
        notification.success({
          message: t("users.labels.passwordChanged"),
        });
        form.reset();
      })
      .catch((errors) => handleErrors(errors, form.setError, notification));
  };

  return (
    <div className="user-settings-card flex flex-col gap-300">
      <div className="flex items-center gap-200">
        <BaseSvgRenderer
          component={LockIcon}
          stroke="var(--base-black)"
          strokeWidth={1.8}
          width="20"
          height="20"
        />
        <h3 className="header-m-medium">{t("users.labels.changePassword")}</h3>
      </div>

      <FormFieldWrapper label={t("users.labels.oldPassword")}>
        <HookFormInput
          name="oldPassword"
          type="password"
          control={form.control}
        />
      </FormFieldWrapper>
      <FormFieldWrapper label={t("users.labels.newPassword")}>
        <HookFormInput
          name="newPassword"
          type="password"
          control={form.control}
        />
      </FormFieldWrapper>
      <FormFieldWrapper label={t("users.labels.confirmPassword")}>
        <HookFormInput
          name="confirmPassword"
          type="password"
          control={form.control}
        />
      </FormFieldWrapper>

      <BaseButton
        className="self-start"
        disabled={isLoading}
        onClick={form.handleSubmit(onSubmit)}
      >
        {t("users.labels.updatePassword")}
      </BaseButton>
    </div>
  );
};

export default ChangePasswordForm;
